export type AudioEngineAudioDeviceInfo = {
    audioEngineDeviceId: string
    deviceUid: string // jitsi fork of portaudio has this field.
    name: string
    maxInputChannels: number
    maxOutputChannels: number
    defaultSampleRate: number
    defaultLowInputLatency: number
    defaultLowOutputLatency: number
    defaultHighInputLatency: number
    defaultHighOutputLatency: number
    hostApiName: 'Windows WASAPI' | 'Core Audio' | 'ASIO'
}

export enum AudioEngineOutputDeviceType {
    PA_PRIMARY = 0,
    PA_SECONDARY = 1,
    PA_REMOTE_RECORDING = 2,
    FILE_MASTER_CAPTURE = 3,
    FILE_LOCAL_LOOPBACK = 4,
    FILE_REMOTE_LOOPBACK = 5,
}

export type AudioEngineConnectionState =
    | 'INITIAL'
    | 'CONNECTING'
    | 'CONNECTED'
    | 'CONNECTION_FAILED_RETRY'
    | 'DISCONNECTED_RETRY'
    | 'DISCONNECTED'
    | 'PERMANENT_FAILURE'

export type AudioEngineState = {
    isEnabled: boolean
    isLoaded: boolean
    errorMessage: string | null
    lastConnectionEvent: AudioEngineConnectionState
}

export class AudioEngineError extends Error {
    code: string
    message: string
    debugMessage: string

    constructor(code: string, message: string) {
        super(message)
        this.code = code
        this.message = message
        this.debugMessage = ''
    }
}

export type AudioEngineAudioStreamInfo = {
    streamUuid: string
    channelCount: number
    channelSelection: number[][]
    channelPackingMap: number[]
    channelUnpackingMap: number[]
    sampleRate: number
    bitDepth: number
    isCompressed: boolean
    compressionBitrate: number
}

export type AudioEngineNetworkStats = {
    serverPing: number | null
}

export type AudioEngineRecordingSettings = {
    filePath: string
}

export type AudioEngineStreamStatsEvent = {
    localStreamStats: { [key: string]: AudioEngineLocalStreamStats }
    remoteStreamStats: { [key: string]: AudioEngineRemoteStreamStats }
}

export class AudioEngineLocalStreamStats {
    volume: number[] = [] // volume per channel
    measuredSampleRate: number = 0
    expectedSampleRate: number = 0
    unresponsiveTimeMs: number = 0
    processingTimeUs: number = 0
    sampleRateMismatchWarning: boolean = false
    streamUnresponsiveWarning: boolean = false
}

export class AudioEngineRemoteStreamStats {
    volume: number[] = [] // volume per channel

    bufferHeadMs: number = 0
    bufferWaterLineMs: number = 0
    bufferOverflowLineMs: number = 0

    totalOverflowCt: number = 0
    totalUnderflowCt: number = 0
    recentOverflowCt: number = 0
    recentUnderflowCt: number = 0
    hardOverflowCt: number = 0 // overflow which was not accommodated by dynamic buffer

    recvPacketRetryLateCt: number = 0
    recvPacketRetryLostCt: number = 0
    recvPacketRetryReqdCt: number = 0
    recvPacketRetrySuccessCt: number = 0

    duplicatePacketCt: number = 0
    outOfOrderPacketCt: number = 0
    latePacketCt: number = 0

    pctLostAudio3s: number = 0
    connectionState: string = ''
}

export type AudioPatch = {
    readonly sourceStreamId: string
    readonly targetDeviceId: string
    readonly targetChannel: number
}
